import Cookies from 'js-cookie';

export const OIDC_BASE = process.env.exzeoid_base_url
export const OIDC_TOKEN_URL = `${OIDC_BASE}/oidc/token`;
export const OIDC_AUTH_URL = `${OIDC_BASE}/oidc/auth`;
export const CLIENT_ID = process.env.exzeoid_client_id
export const CLIENT_SECRET = process.env.exzeoid_client_secret

export const POST_LOGOUT_REDIRECT_URI = () =>
  `${window.location.origin}/signedout`;

export const buildLogoutURL = (returnTo) => {
  const tokenResponse = parseAuToken();
  const logoutURL = new URL(`${OIDC_BASE}/v2/logout`);
  logoutURL.searchParams.set("client_id", CLIENT_ID);
  if (tokenResponse?.id_token) {
    logoutURL.searchParams.set("id_token_hint", tokenResponse.id_token);
  }
  logoutURL.searchParams.set(
    "post_logout_redirect_uri",
    returnTo || POST_LOGOUT_REDIRECT_URI(),
  );
  return logoutURL.toString();
}

export const decodeJwtPayload = (token) => {
    try {
        if (!token || typeof token !== 'string') return null;
        const parts = token.split('.');
        if (parts.length < 2) return null;
        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64 + '==='.slice((base64.length + 3) % 4);
        const json = decodeURIComponent(
            atob(padded)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        return JSON.parse(json);
    } catch (e) {
        console.error('Failed to decode JWT payload:', e);
        return null;
    }
};

const StrictCookies = Cookies.withConverter({
    write: (value) => encodeURIComponent(value),
});

const RawCookies = Cookies.withConverter({
    write: (value) => value,
    read: (value) => value,
});

const AUTH_ARTIFACT_KEYS = [
    '_au',
    '_co',
    'exzeoid',
    'access_token',
    'refresh_token',
    '_evw',
    '_eva',
    'Show_Is_Email_Verified',
    'Show_Is_Toast_Displayed',
];

const candidateCookieDomains = () => {
    const domains = [undefined];
    try {
        const host = window.location.hostname.toLowerCase();
        // sharedCookieDomain() can write a Domain=localhost cookie from
        // either "localhost" or "admin.localhost" — neither is reachable
        // from the label-peeling loop below (it bails on hosts with no dot,
        // and a 2-label admin.localhost only peels back to itself), so
        // sweep it explicitly whenever we're on either of those hosts.
        if (host === 'localhost' || host.endsWith('.localhost')) {
            domains.push('localhost', '.localhost');
        }
        // No further domain-scoped cookies possible on a bare host or an IP literal.
        if (!host || !host.includes('.') || /^[\d.]+$/.test(host)) return domains;
        const labels = host.split('.');
        for (let i = 0; i <= labels.length - 2; i += 1) {
            const domain = labels.slice(i).join('.');
            domains.push(domain, `.${domain}`);
        }
    } catch (e) { /* noop */ }
    return domains;
};

export const clearAuthArtifacts = () => {
    try {
        AUTH_ARTIFACT_KEYS.forEach((k) => {
            try { localStorage.removeItem(k); } catch (e) { /* noop */ }
            try { sessionStorage.removeItem(k); } catch (e) { /* noop */ }
        });
    } catch (e) {
        console.error('Failed to clear auth artifacts from localStorage:', e);
    }
    try {
        const domains = candidateCookieDomains();
        AUTH_ARTIFACT_KEYS.forEach((k) => {
            domains.forEach((domain) => {
                ['/', undefined].forEach((path) => {
                    try {
                        const options = {};
                        if (path) options.path = path;
                        if (domain) options.domain = domain;
                        Cookies.remove(k, options);
                    } catch (e) { /* noop */ }
                });
            });
        });
    } catch (e) {
        console.error('Failed to clear auth artifacts from cookies:', e);
    }
};

// Admin is its own subdomain of the app's own base domain (siteUrl=
// "justezapp.com", admin_site_url="admin.justezapp.com" — see .env and
// actionCenter.jsx, which already builds the admin link from these two). A
// host-only cookie (no Domain attribute) written while on one of those
// hosts is invisible on the other, which is why switching to admin looked
// like the session had vanished. Note the main app itself runs on the bare
// 2-label siteUrl (no subdomain) for most logins, so guessing the shared
// domain from window.location.hostname's label count (peel-one-label) is
// wrong on that host — it must come from the configured siteUrl instead.
const sharedCookieDomain = () => {
    try {
        const host = window.location.hostname.toLowerCase();

        // The committed .env's siteUrl is always the prod value (no
        // .env.local/.env.development exists in this repo to override it
        // per developer), so deriving purely from siteUrl leaves every
        // local dev run unfixed. "localhost"/"*.localhost" can only ever
        // mean "this machine" — there's no other real host it could collide
        // with — so it's safe to special-case directly off the current host
        // rather than requiring a manual siteUrl override nobody documents.
        if ((host === 'localhost' || host === 'admin.localhost') || host.endsWith('.localhost')) {
            return host;
        }

        const siteUrl = process.env.siteUrl;
        if (!siteUrl) return undefined;
        const base = siteUrl.replace(/^https?:\/\//, '').split('/')[0].split(':')[0].toLowerCase();
        if (!base || !base.includes('.') || /^[\d.]+$/.test(base)) return undefined;
        // Only scope to `base` if we're actually running on it (or a
        // subdomain of it) — never set Domain to something that doesn't
        // match where the app is currently served from.
        if (host !== base && !host.endsWith(`.${base}`)) return undefined;
        return base;
    } catch (e) {
        return undefined;
    }
};

export const persistAuthArtifacts = (tokenResponse, organization) => {
    if (!tokenResponse || !tokenResponse.access_token) return;
    const payload = decodeJwtPayload(tokenResponse.access_token) || {};
    const exzeoId = payload.aud || '';
    const orgId = organization ? `${organization.org_id ?? organization.orgId ?? organization.id ?? ""}`.trim() : "";
    const auSerialized = JSON.stringify(tokenResponse);

    try {
        const cookieOpts = { sameSite: 'Lax' };
        const domain = sharedCookieDomain();
        if (domain) cookieOpts.domain = domain;
        StrictCookies.set('_au', auSerialized, cookieOpts);
        if (orgId) RawCookies.set('_co', orgId, cookieOpts);
        if (exzeoId) RawCookies.set('exzeoid', exzeoId, cookieOpts);
    } catch (e) {
        console.error('Failed to write auth artifacts to cookies:', e);
    }
};

export const parseAuToken = () => {
    let raw = null;
    try {
        raw = Cookies.get('_au') || null;
    } catch (e) { /* noop */ }
    if (!raw || typeof raw !== 'string') return null;
    const trimmed = raw.trim();
    if (!trimmed.startsWith('{')) {
        return { access_token: trimmed };
    }
    try {
        return JSON.parse(trimmed);
    } catch (e) {
        return null;
    }
};

export async function exchangeCode(code) {
  const body = new URLSearchParams();
  body.set("grant_type", "authorization_code");
  body.set("client_id", CLIENT_ID);
  body.set("client_secret", CLIENT_SECRET);
  body.set("redirect_uri", window.location.origin);
  body.set("code", code);

  const response = await fetch(`${OIDC_TOKEN_URL}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: body.toString(),
  });

  const contentType = response.headers.get("content-type") ?? "";
  const data = contentType.includes("application/json") ? await response.json() : await response.text();
  if (!response.ok || !data?.access_token) {
    const message =
      typeof data === "string" ? data : data?.error_description ?? data?.message ?? "Authentication failed";
    throw new Error(message);
  }

  const organizations = await resolveOrganizations(data);
  if (!organizations.length) {
    throw new Error("No organization access found for this user");
  }

  const selectedOrganization = organizations.length === 1 ? organizations[0] : null;
  if (selectedOrganization) {
    finalizeAuth(data, selectedOrganization);
  }

  return {
    tokenResponse: data,
    organizations,
    selectedOrganization,
    userId: selectedOrganization?.user_id || 0
  };
}

export function finalizeAuth(tokenResponse, organization) {
  clearAuthArtifacts();
  persistAuthArtifacts(tokenResponse, organization);
}

export async function isMultiOrgUser(data){
    const organizations = await resolveOrganizations(data);
    if (!organizations.length) {
        throw new Error("No organization access found for this user");
    }

    const selectedOrganization = organizations.length === 1 ? organizations[0] : null;
    if (selectedOrganization) {
        finalizeAuth(data, selectedOrganization);
    }
}

function normalizeOrgId(organization) {
  if (!organization) {
    return "";
  }

  return `${organization.org_id ?? organization.orgId ?? organization.id ?? ""}`.trim();
}

function normalizeProfileUrl(value) {
  const raw = `${value ?? ""}`.trim();
  if (!raw) {
    return "";
  }

  if (raw.startsWith("//")) {
    return `${window.location.protocol}${raw}`;
  }
  if (raw.startsWith("/")) {
    return `${window.location.origin}${raw}`;
  }

  try {
    const url = new URL(raw);
    if (url.protocol && url.host) {
      return url.toString();
    }
  } catch (_error) {
    // Fall through to best-effort normalization below.
  }

  return raw.replace("https:///", `${window.location.origin}/`);
}

export async function resolveOrganizations(tokenResponse) {
  const organizations = await fetchLoginOrganizations(tokenResponse.access_token);
  const dedupedOrganizations = [];
  const seen = new Set();

  for (const organization of organizations) {
    const orgId = normalizeOrgId(organization);
    if (!orgId || seen.has(orgId)) {
      continue;
    }
    seen.add(orgId);
    dedupedOrganizations.push(organization);
  }

  if (!dedupedOrganizations.length) {
    throw new Error("No organization access found for this user");
  }

  return dedupedOrganizations;
}

async function gatewayGET(path, accessToken, org_id) {
  const response = await fetch(`${process.env.core_gateway_url}${path}`, {
    headers: {
        Authorization: `Bearer ${accessToken}`,
        source: "web",
        "x-xzo-source": "web",
        "org-id": org_id
    },
  });

  const contentType = response.headers.get("content-type") ?? "";
  const data = contentType.includes("application/json") ? await response.json() : await response.text();
  if (!response.ok) {
    const message =
      typeof data === "string" ? data : data?.message ?? data?.error ?? "Gateway request failed";
    const error = new Error(message);
    error.status = response.status;
    error.body = data;
    throw error;
  }
  return data;
}

async function fetchLoginOrganizations(accessToken) {
    const dataUser = await gatewayGET("/2.0/user?type=login", accessToken);
    const loginOrganizations = normalizeOrganizations(dataUser);
    if (loginOrganizations.length) {
        return loginOrganizations;
    }

    try {
        const dataOrg = await gatewayGET("/2.0/user/orgs", accessToken);
        const organizations = normalizeOrganizations(dataOrg);
        if (organizations.length) {
        return organizations;
        }
    } catch (error) {
        if (error?.status !== 404) {
        throw error;
        }
    }

    const dataUserOrg = await gatewayGET("/2.0/user?type=ORGANIZATIONS", accessToken);
    return normalizeOrganizations(dataUserOrg);
}

export async function fetchUser(accessToken, org_id){
  return await gatewayGET("/2.0/user/detail", accessToken, org_id);
}

function normalizeOrganizations(payload) {
  const rawOrganizations = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.organization)
      ? payload.organization
      : Array.isArray(payload?.organizations)
        ? payload.organizations
        : payload?.organization
          ? [payload.organization]
          : payload?.organizations
            ? [payload.organizations]
            : [];

  const organizations = rawOrganizations.filter(Boolean);
  return organizations.map((organization) => ({
    user_id: `${organization.user_id ?? organization.userId ?? ""}`.trim(),
    org_id: normalizeOrgId(organization),
    name: `${organization.name ?? ""}`.trim(),
    profile_url: normalizeProfileUrl(organization.profile_url ?? organization.profileImage ?? ""),
    address1: `${organization.address1 ?? organization.address_line1 ?? ""}`.trim(),
    address2: `${organization.address2 ?? organization.address_line2 ?? ""}`.trim(),
    city: `${organization.city ?? ""}`.trim(),
    state: `${organization.state ?? ""}`.trim(),
    zip: `${organization.zip ?? ""}`.trim(),
    country: `${organization.country ?? ""}`.trim(),
  }));
}